import { useEffect, useState } from "react";
import {
  Shield, Globe, Smartphone, Wallet, BadgeCheck, Loader2, Trash2, Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";

function TrustBadge({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${
        ok ? "bg-up/10 text-up" : "bg-surface-2 text-muted-foreground"
      }`}
    >
      {ok ? <BadgeCheck size={12} /> : null}
      {label}
    </span>
  );
}

export default function TrustCenter({ onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ip, setIp] = useState("");
  const [ipLabel, setIpLabel] = useState("");
  const [ipCode, setIpCode] = useState("");
  const [ipStep, setIpStep] = useState("idle");
  const [devCode, setDevCode] = useState("");
  const [devStep, setDevStep] = useState("idle");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/api/trust/status");
      setStatus(r.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const requestIp = async () => {
    if (!ip.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/api/trust/ip/request", {
        ip: ip.trim(),
        label: ipLabel.trim() || ip.trim(),
      });
      if (r.data.dev_otp) setIpCode(r.data.dev_otp);
      setIpStep("verify");
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyIp = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/trust/ip/verify", {
        ip: ip.trim(),
        code: ipCode.trim(),
        label: ipLabel.trim() || ip.trim(),
      });
      setIpStep("idle");
      setIp("");
      setIpCode("");
      load();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const requestDevice = async () => {
    setBusy(true);
    setError("");
    try {
      const deviceId = getDeviceId();
      const r = await api.post("/api/trust/device/request", {
        device_id: deviceId,
        label: "This browser",
        device_name: navigator.userAgent?.slice(0, 80) || "Web",
      });
      if (r.data.dev_otp) setDevCode(r.data.dev_otp);
      setDevStep("verify");
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyDevice = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/trust/device/verify", {
        device_id: getDeviceId(),
        code: devCode.trim(),
      });
      setDevStep("idle");
      setDevCode("");
      load();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const removeIp = async (id) => {
    if (!window.confirm("Remove this trusted IP?")) return;
    await api.delete(`/api/trust/ip/${id}`);
    load();
  };

  const removeDevice = async (id) => {
    if (!window.confirm("Remove this trusted device?")) return;
    await api.delete(`/api/trust/device/${id}`);
    load();
  };

  if (loading && !status) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  const s = status || {};

  return (
    <div className="space-y-5 p-1">
      <p className="text-[13px] text-muted-foreground">
        Unlock P2P &amp; OTC trading when <b>any</b> trust method is satisfied: KYC, deposit wallet whitelist,
        trusted IP, or trusted device.
      </p>

      <div className="flex flex-wrap gap-2">
        <TrustBadge ok={s.kyc_verified} label="KYC" />
        <TrustBadge ok={s.trusted_wallet} label="Deposit wallet" />
        <TrustBadge ok={s.trusted_ip} label="Trusted IP" />
        <TrustBadge ok={s.trusted_device} label="Trusted device" />
        <TrustBadge ok={s.can_trade} label={s.can_trade ? "Trading unlocked" : "Trading locked"} />
      </div>

      {!s.can_trade && (
        <div className="rounded-lg bg-warning/10 border border-warning/30 p-3 text-[12px] text-warning">
          Complete one method below or{" "}
          <Link to="/profile/kyc" className="font-semibold text-primary underline">
            verify KYC
          </Link>
          . Deposit funds unlock after you whitelist the deposit address in{" "}
          <Link to="/wallets" className="font-semibold text-primary underline">
            Wallets
          </Link>
          .
        </div>
      )}

      {error && (
        <div className="text-[12px] text-down bg-down/10 border border-down/30 rounded-md p-3">
          {error}
        </div>
      )}

      {/* Trusted IP */}
      <section className="border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-foreground text-[14px]">
          <Globe size={16} /> Trusted IP
        </div>
        <ul className="space-y-2 text-[12px]">
          {(s.trusted_ips || []).map((row) => (
            <li key={row.id} className="flex justify-between items-center bg-cm-surface/50 rounded-md px-3 py-2">
              <span>
                {row.label} — <span className="font-mono">{row.ip}</span>{" "}
                <span className="text-muted-foreground">({row.status})</span>
              </span>
              <button type="button" onClick={() => removeIp(row.id)} className="text-down">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
        {ipStep === "idle" ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              className="flex-1 px-3 py-2 rounded-md border text-[13px] font-mono"
              placeholder="Your public IP (e.g. 203.0.113.5)"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
            <input
              className="flex-1 px-3 py-2 rounded-md border text-[13px]"
              placeholder="Label"
              value={ipLabel}
              onChange={(e) => setIpLabel(e.target.value)}
            />
            <button
              type="button"
              disabled={busy || !ip.trim()}
              onClick={requestIp}
              className="px-4 py-2 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold disabled:opacity-50"
            >
              <Plus size={14} className="inline" /> Send email code
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              className="flex-1 px-3 py-2 rounded-md border text-[13px] font-mono"
              placeholder="Email OTP"
              value={ipCode}
              onChange={(e) => setIpCode(e.target.value)}
            />
            <button
              type="button"
              disabled={busy}
              onClick={verifyIp}
              className="px-4 py-2 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold"
            >
              Verify IP
            </button>
          </div>
        )}
      </section>

      {/* Trusted device */}
      <section className="border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-foreground text-[14px]">
          <Smartphone size={16} /> Trusted device
        </div>
        <ul className="space-y-2 text-[12px]">
          {(s.trusted_devices || []).map((row) => (
            <li key={row.id} className="flex justify-between items-center bg-cm-surface/50 rounded-md px-3 py-2">
              <span className="font-mono text-[11px]">{row.device_id?.slice(0, 16)}… ({row.status})</span>
              <button type="button" onClick={() => removeDevice(row.id)} className="text-down">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
        {devStep === "idle" ? (
          <button
            type="button"
            disabled={busy}
            onClick={requestDevice}
            className="px-4 py-2 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold"
          >
            Trust this browser (email OTP)
          </button>
        ) : (
          <div className="flex gap-2">
            <input
              className="flex-1 px-3 py-2 rounded-md border text-[13px] font-mono"
              placeholder="Email OTP"
              value={devCode}
              onChange={(e) => setDevCode(e.target.value)}
            />
            <button
              type="button"
              disabled={busy}
              onClick={verifyDevice}
              className="px-4 py-2 rounded-md brand-gradient text-primary-foreground text-[12px] font-semibold"
            >
              Verify device
            </button>
          </div>
        )}
      </section>

      <section className="border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 font-semibold text-foreground text-[14px] mb-2">
          <Wallet size={16} /> Deposit wallet trust
        </div>
        <p className="text-[12px] text-muted-foreground">
          Whitelist a deposit address in Wallet → Whitelist. After the cooling period, deposit wallet trust
          unlocks trading.
        </p>
      </section>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="w-full py-2.5 rounded-lg border border-border text-[13px] font-semibold"
        >
          Done
        </button>
      )}
    </div>
  );
}
